/**
 * @file index.tsx
 *
 * @fileoverview Displays a google places API Autocomplete component.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import HiddenVisually from "../../14-w-a11y/02-visually-hidden";
import styles from "./googlePlaces.module.scss";
import { cva } from "class-variance-authority";
import type { GoogleMaps, GooglePlacesAutocompleteHandle, GooglePlacesAutocompleteProps, PlacesLibrary, Suggest, LocationSuggest, AutocompleteRequest, GeocoderResult } from "./model";
import { escapeRegExp, getBoundsLiteral, getLocationLiteral, mapTypes, mapCountry, mapPlaceFields, mapMatches, getSuggestionLabel, mapPlaceToLegacyResult } from "./helpers";
export type { GooglePlacesAutocompleteHandle, GooglePlacesAutocompleteProps, Fixture, Suggest, LocationSuggest } from "./model";

const inputIcon = cva([styles.inputIcon], {
  variants: {
    iconPosition: {
      start: styles.iconStart,
      end: styles.iconEnd,
    },
  },
  defaultVariants: {
    iconPosition: "start",
  },
});

const autocompleteInputWrapper = cva([styles.googleAutocompleteInputWrapper], {
  variants: {
    iconPosition: {
      start: styles.inputIconStart,
      end: styles.inputIconEnd,
    },
  },
});

const formatMatchedText = (userInput: string, suggest: Suggest) => {
  if (!userInput || !suggest.matchedSubstrings) {
    return suggest.label;
  }

  const parts: React.ReactNode[] = [];
  let previousEnd = 0;

  suggest.matchedSubstrings.forEach(({ offset, length }, index) => {
    const end = offset + length;

    if (offset > previousEnd) {
      parts.push(suggest.label.substring(previousEnd, offset));
    }

    parts.push(
      <b className="geosuggest__item__matched-text" key={`${offset}-${index}`}>
        {suggest.label.substring(offset, end)}
      </b>,
    );

    previousEnd = end;
  });

  if (previousEnd < suggest.label.length) {
    parts.push(suggest.label.substring(previousEnd));
  }

  return <span>{parts}</span>;
};

const defaultSkipSuggest = () => false;

export const GooglePlacesAutocomplete = forwardRef<
  GooglePlacesAutocompleteHandle,
  GooglePlacesAutocompleteProps
>(
  (
    {
      autoActivateFirstSuggest = false,
      autoComplete = "off",
      bounds,
      className,
      country,
      disabled = false,
      disableSuggests = false,
      fixtures = [],
      focusElements = null,
      getSuggestLabel,
      googleMaps,
      highlightMatch = true,
      id = "geoautosuggest",
      ignoreEnter = false,
      ignoreTab = false,
      initialValue = "",
      inputClassName,
      inputType = "text",
      label = "Geo autocomplete",
      location,
      maxFixtures = 10,
      minLength = 1,
      onActivateSuggest,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      onSuggestNoResults,
      onSuggestSelect,
      onUpdateSuggests,
      placeDetailFields,
      placeholder = "Search places",
      queryDelay = 250,
      radius,
      refElement,
      renderSuggestItem,
      required,
      size = 40,
      skipSuggest = defaultSkipSuggest,
      style = {},
      suggestItemActiveClassName,
      suggestItemClassName,
      suggestsClassName,
      suggestsHiddenClassName,
      types,
      Icon = null,
      iconPosition = "start",
      ...inputProps
    },
    forwardedRef,
  ) => {
    const [activeSuggest, setActiveSuggest] = useState<Suggest | null>(null);
    const [ignoreBlurState, setIgnoreBlurState] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuggestsHidden, setIsSuggestsHidden] = useState(true);
    const [showFocusElements, setShowFocusElements] = useState(false);
    const [suggests, setSuggests] = useState<Suggest[]>([]);
    const [userInput, setUserInput] = useState(initialValue || "");
    const inputRef = useRef<HTMLInputElement | null>(null);
    const hideTimerRef = useRef<number | undefined>(undefined);
    const searchTimerRef = useRef<number | undefined>(undefined);
    const searchIdRef = useRef(0);
    const sessionTokenRef = useRef<object | undefined>(undefined);
    const placesLibraryRef = useRef<PlacesLibrary | undefined>(undefined);
    const googleMapsRef = useRef<GoogleMaps | null>(null);

    const listId = `geosuggest__list${id ? `--${id}` : ""}`;
    const listLabel = label ? `${label} options` : "options";

    const publicHandle = useMemo(
      () => ({
        blur: () => inputRef.current?.blur(),
        clear: () => {
          setUserInput("");
          setIsSuggestsHidden(true);
          setActiveSuggest(null);
          setSuggests([]);
        },
        focus: () => inputRef.current?.focus(),
        get state() {
          return { userInput };
        },
        update: (value: string) => {
          setUserInput(value);
          onChange?.(value);
        },
      }),
      [onChange, userInput],
    );

    useImperativeHandle(forwardedRef, () => publicHandle, [publicHandle]);
    useImperativeHandle(refElement, () => publicHandle, [publicHandle]);

    const getGoogleMaps = useCallback(() => {
      if (googleMapsRef.current) {
        return googleMapsRef.current;
      }

      const maps =
        googleMaps ||
        (typeof window !== "undefined"
          ? (window as unknown as Window & { google?: { maps?: GoogleMaps } }).google?.maps
          : null);

      if (!maps) {
        console.error("Google maps API was not found in the page.");
        return null;
      }

      googleMapsRef.current = maps;
      return maps;
    }, [googleMaps]);

    const getPlacesLibrary = useCallback(async () => {
      if (placesLibraryRef.current) {
        return placesLibraryRef.current;
      }

      const maps = getGoogleMaps();
      if (!maps) {
        return null;
      }

      if (maps.importLibrary) {
        placesLibraryRef.current = await maps.importLibrary("places");
      } else if (
        maps.places?.AutocompleteSuggestion &&
        maps.places?.AutocompleteSessionToken
      ) {
        placesLibraryRef.current = maps.places;
      } else {
        throw new Error(
          "Google Places API (New) is unavailable. Load the Maps JavaScript API with the places library and a version that supports AutocompleteSuggestion.",
        );
      }

      return placesLibraryRef.current;
    }, [getGoogleMaps]);

    const getSessionToken = useCallback(async () => {
      const places = await getPlacesLibrary();

      if (!places) {
        return undefined;
      }

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }

      return sessionTokenRef.current;
    }, [getPlacesLibrary]);

    const updateSuggestions = useCallback(
      (
        googleSuggests: Suggest[] = [],
        callback?: (nextSuggests: Suggest[]) => void,
        inputValue = userInput,
      ) => {
        const regex = new RegExp(escapeRegExp(inputValue), "gim");
        let fixturesSearched = 0;
        const fixtureSuggests = fixtures.reduce<Suggest[]>((items, fixture) => {
          if (maxFixtures && fixturesSearched >= maxFixtures) {
            return items;
          }

          if (!skipSuggest(fixture) && fixture.label.match(regex)) {
            fixturesSearched++;
            items.push({
              ...fixture,
              isFixture: true,
              matchedSubstrings: [
                {
                  length: inputValue.length,
                  offset: fixture.label.search(regex),
                },
              ],
              placeId: fixture.placeId || fixture.label,
            });
          }

          return items;
        }, []);

        const nextSuggests = [...fixtureSuggests, ...googleSuggests].filter(
          (suggest) => !skipSuggest(suggest),
        );
        const nextActiveSuggest =
          activeSuggest &&
          nextSuggests.find(
            (suggest) =>
              suggest.placeId === activeSuggest.placeId &&
              suggest.isFixture === activeSuggest.isFixture,
          )
            ? activeSuggest
            : null;

        setSuggests(nextSuggests);
        setActiveSuggest(nextActiveSuggest);
        onUpdateSuggests?.(nextSuggests, nextActiveSuggest);

        if (nextSuggests.length === 0) {
          onSuggestNoResults?.(inputValue);
        }

        callback?.(nextSuggests);
      },
      [
        activeSuggest,
        fixtures,
        maxFixtures,
        onSuggestNoResults,
        onUpdateSuggests,
        skipSuggest,
        userInput,
      ],
    );

    const buildRequest = useCallback(
      async (input: string) => {
        const token = await getSessionToken();
        const locationLiteral = getLocationLiteral(location);
        const boundsLiteral = getBoundsLiteral(bounds);
        const request: AutocompleteRequest = {
          input,
          sessionToken: token,
        };
        const includedPrimaryTypes = mapTypes(types);
        const includedRegionCodes = mapCountry(country);

        if (includedPrimaryTypes) {
          request.includedPrimaryTypes = includedPrimaryTypes;
        }

        if (includedRegionCodes && includedRegionCodes.length > 0) {
          request.includedRegionCodes = includedRegionCodes;
        }

        if (boundsLiteral) {
          request.locationBias = boundsLiteral;
        } else if (locationLiteral) {
          request.locationBias = radius
            ? {
                center: locationLiteral,
                radius: Number(radius),
              }
            : locationLiteral;
        }

        return request;
      },
      [bounds, country, getSessionToken, location, radius, types],
    );

    const searchSuggests = useCallback(async (inputValue = userInput) => {
      const currentSearchId = searchIdRef.current + 1;
      searchIdRef.current = currentSearchId;

      if (disableSuggests) {
        setSuggests([]);
        setActiveSuggest(null);
        onUpdateSuggests?.([], null);
        return;
      }

      if (!inputValue) {
        updateSuggestions([], undefined, inputValue);
        return;
      }

      if (minLength && inputValue.length < minLength) {
        updateSuggestions([], undefined, inputValue);
        return;
      }

      setIsLoading(true);

      try {
        const places = await getPlacesLibrary();
        if (!places?.AutocompleteSuggestion) {
          throw new Error("AutocompleteSuggestion is unavailable.");
        }

        const request = await buildRequest(inputValue);
        const response =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request,
          );

        if (searchIdRef.current !== currentSearchId) {
          return;
        }

        const googleSuggests: Suggest[] = (response?.suggestions || [])
          .filter((suggestion) => Boolean(suggestion.placePrediction))
          .map((suggestion) => {
            const placePrediction = suggestion.placePrediction!;
            const label = getSuggestLabel
              ? getSuggestLabel({
                  description: getSuggestionLabel(placePrediction),
                  matched_substrings: mapMatches(
                    placePrediction.text?.matches ||
                      placePrediction.mainText?.matches,
                  ),
                  place_id: placePrediction.placeId || placePrediction.id,
                })
              : getSuggestionLabel(placePrediction);

            return {
              description: label,
              isFixture: false,
              label,
              matchedSubstrings: mapMatches(
                placePrediction.text?.matches ||
                  placePrediction.mainText?.matches,
              ),
              placeId:
                placePrediction.placeId || placePrediction.id || label,
              prediction: placePrediction,
            };
          });

        updateSuggestions(googleSuggests, (nextSuggests) => {
          if (autoActivateFirstSuggest && !activeSuggest) {
            setActiveSuggest(nextSuggests[0] || null);
          }
        }, inputValue);
      } catch (error) {
        console.error(error);
        updateSuggestions([], undefined, inputValue);
      } finally {
        if (searchIdRef.current === currentSearchId) {
          setIsLoading(false);
        }
      }
    }, [
      activeSuggest,
      autoActivateFirstSuggest,
      buildRequest,
      disableSuggests,
      getPlacesLibrary,
      getSuggestLabel,
      minLength,
      onUpdateSuggests,
      updateSuggestions,
      userInput,
    ]);

    const showSuggests = useCallback(() => {
      window.clearTimeout(hideTimerRef.current);
      setIsSuggestsHidden(false);
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = window.setTimeout(
        () => searchSuggests(userInput),
        queryDelay,
      );
    }, [queryDelay, searchSuggests, userInput]);

    const hideSuggests = useCallback(() => {
      hideTimerRef.current = window.setTimeout(() => {
        setActiveSuggest(null);
        setIsSuggestsHidden(true);
      }, 100);
    }, []);

    const selectSuggest = useCallback(
      async (suggestToSelect?: Suggest | null) => {
        let suggest =
          suggestToSelect ||
          (autoActivateFirstSuggest && suggests.length > 0
            ? suggests[0]
            : {
                isFixture: true,
                label: userInput,
                placeId: userInput,
              });

        setIsSuggestsHidden(true);
        setUserInput(
          typeof suggest.label !== "object"
            ? suggest.label
            : suggest.description || "",
        );

        if (suggest.location) {
          setIgnoreBlurState(false);
          onSuggestSelect?.(suggest as LocationSuggest);
          return;
        }

        if (suggest.prediction?.toPlace) {
          try {
            const place = suggest.prediction.toPlace();
            await place.fetchFields({
              fields: mapPlaceFields(placeDetailFields),
            });

            const placeLocation = place.location;
            if (!placeLocation) {
              throw new Error("Selected place has no location.");
            }
            const selectedSuggest: LocationSuggest = {
              ...suggest,
              gmaps: mapPlaceToLegacyResult(place),
              location: {
                lat:
                  typeof placeLocation.lat === "function"
                    ? placeLocation.lat()
                    : placeLocation.lat,
                lng:
                  typeof placeLocation.lng === "function"
                    ? placeLocation.lng()
                    : placeLocation.lng,
              },
            };

            sessionTokenRef.current = undefined;
            onSuggestSelect?.(selectedSuggest);
          } catch (error) {
            console.error(error);
          }

          setIgnoreBlurState(false);
          return;
        }

        const maps = getGoogleMaps();

        if (maps?.Geocoder) {
          const geocoder = new maps.Geocoder();
          geocoder.geocode(
            {
              address: suggest.label,
              bounds,
              componentRestrictions: country ? { country } : undefined,
              location,
            },
            (results: GeocoderResult[], status: string) => {
              if (status === maps.GeocoderStatus?.OK && results?.[0]) {
                const gmaps = results[0];
                const placeLocation = gmaps.geometry?.location;

                if (placeLocation) onSuggestSelect?.({
                  ...suggest,
                  gmaps,
                  location: {
                    lat: placeLocation.lat(),
                    lng: placeLocation.lng(),
                  },
                });
              }

              setIgnoreBlurState(false);
            },
          );
          return;
        }

        setIgnoreBlurState(false);
      },
      [
        autoActivateFirstSuggest,
        bounds,
        country,
        getGoogleMaps,
        location,
        onSuggestSelect,
        placeDetailFields,
        suggests,
        userInput,
      ],
    );

    const activateSuggest = useCallback(
      (direction: "next" | "prev") => {
        if (isSuggestsHidden) {
          showSuggests();
          return;
        }

        const suggestsCount = suggests.length - 1;
        const currentIndex = suggests.findIndex(
          (suggest) =>
            activeSuggest &&
            suggest.placeId === activeSuggest.placeId &&
            suggest.isFixture === activeSuggest.isFixture,
        );
        let nextIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

        if (!activeSuggest) {
          nextIndex = direction === "next" ? 0 : suggestsCount;
        }

        const nextActiveSuggest =
          nextIndex >= 0 && nextIndex <= suggestsCount
            ? suggests[nextIndex]
            : null;

        onActivateSuggest?.(nextActiveSuggest);
        setActiveSuggest(nextActiveSuggest);
      },
      [
        activeSuggest,
        isSuggestsHidden,
        onActivateSuggest,
        showSuggests,
        suggests,
      ],
    );

    useEffect(() => {
      setUserInput(initialValue || "");
    }, [initialValue]);

    useEffect(() => {
      if (!isSuggestsHidden) {
        showSuggests();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixtures]);

    useEffect(
      () => () => {
        window.clearTimeout(hideTimerRef.current);
        window.clearTimeout(searchTimerRef.current);
      },
      [],
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      searchIdRef.current += 1;

      if (!value) {
        onSuggestSelect?.();
      }

      setUserInput(value);
      setIsSuggestsHidden(false);
      onChange?.(value);
      window.clearTimeout(searchTimerRef.current);
      searchTimerRef.current = window.setTimeout(() => searchSuggests(value), queryDelay);
    };

    const handleFocus = () => {
      onFocus?.();
      if (focusElements) {
        setShowFocusElements(true);
      }
      showSuggests();
    };

    const handleBlur = () => {
      if (!ignoreBlurState) {
        hideSuggests();
      }
      onBlur?.(userInput);

      if (focusElements) {
        window.setTimeout(() => {
          setShowFocusElements(false);
        }, 50);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);

      switch (event.key) {
        case "ArrowDown":
          if (!event.shiftKey) {
            event.preventDefault();
            activateSuggest("next");
          }
          break;

        case "ArrowUp":
          if (!event.shiftKey) {
            event.preventDefault();
            activateSuggest("prev");
          }
          break;

        case "Enter":
          if (!ignoreEnter) {
            selectSuggest(activeSuggest);
          }
          break;

        case "Tab":
          if (!ignoreTab) {
            selectSuggest(activeSuggest);
          }
          break;

        case "Escape":
          hideSuggests();
          break;
      }
    };

    const suggestsHidden = isSuggestsHidden || suggests.length === 0;

    return (
      <div
        className={classNames("geosuggest", className, {
          "geosuggest--loading": isLoading,
        })}
      >
        <div className={styles.googleAutocompleteWrapper}>
          {label && (
            <HiddenVisually>
              <label htmlFor={`geosuggest__input--${id}`}>{label}</label>
            </HiddenVisually>
          )}
          <div
            className={autocompleteInputWrapper({
              iconPosition: Icon ? iconPosition : undefined,
            })}
          >
            <div className="geosuggest__input-wrapper">
              <input
                {...inputProps}
                autoComplete={autoComplete}
                className={classNames("geosuggest__input", inputClassName)}
                disabled={disabled}
                id={`geosuggest__input${id ? `--${id}` : ""}`}
                onBlur={handleBlur}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onKeyPress={onKeyPress}
                placeholder={placeholder}
                ref={inputRef}
                required={required}
                role="combobox"
                size={size}
                style={style.input}
                type={inputType}
                value={userInput}
                aria-activedescendant={activeSuggest?.placeId}
                aria-controls={listId}
                aria-expanded={!suggestsHidden}
                aria-owns={listId}
              />
            </div>
            {Icon && <div className={inputIcon({ iconPosition })}>{Icon}</div>}
          </div>
          <div className="geosuggest__suggests-wrapper">
            <ul
              aria-label={listLabel}
              className={classNames(
                "geosuggest__suggests",
                suggestsClassName,
                {
                  "geosuggest__suggests--hidden": suggestsHidden,
                  [suggestsHiddenClassName || ""]:
                    suggestsHiddenClassName && suggestsHidden,
                },
              )}
              id={listId}
              role="listbox"
              style={style.suggests}
            >
              {suggests.map((suggest) => {
                const isActive =
                  activeSuggest?.placeId === suggest.placeId &&
                  activeSuggest?.isFixture === suggest.isFixture;

                return (
                  <li
                    aria-selected={isActive}
                    className={classNames(
                      "geosuggest__item",
                      suggest.className,
                      suggestItemClassName,
                      {
                        "geosuggest__item--active": isActive,
                        [suggestItemActiveClassName || ""]:
                          suggestItemActiveClassName && isActive,
                      },
                    )}
                    id={suggest.placeId}
                    key={`${suggest.isFixture ? "fixture" : "google"}-${
                      suggest.placeId
                    }`}
                    onClick={(event) => {
                      event.preventDefault();
                      selectSuggest(suggest);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectSuggest(suggest);
                      }
                    }}
                    onMouseDown={() => setIgnoreBlurState(true)}
                    onMouseLeave={() => setIgnoreBlurState(false)}
                    role="option"
                    style={style.suggestItem}
                    tabIndex={-1}
                  >
                    {renderSuggestItem
                      ? renderSuggestItem(suggest)
                      : highlightMatch
                        ? formatMatchedText(userInput, suggest)
                        : suggest.label}
                  </li>
                );
              })}
            </ul>
          </div>
          {showFocusElements && (
            <div className={`focus-elements ${styles.focusElementsWrapp}`}>
              {focusElements}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default GooglePlacesAutocomplete;
